import { useState } from 'react';
import type { Address, Hash, PublicClient } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import MAX_UINT256 from 'constants/maxUint256';
import {
  getContractAddress,
  isolatedPoolComptrollerAbi,
  positionRouterAbi,
  vBep20Abi,
} from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';
import { buffer } from 'utilities';

import type { BittensorNativeSupplySource } from '../../SupplyForm/getBittensorSupplySourceTokens';
import { getVTokenWrapperKind } from '../../shared/getVTokenWrapperKind';
import {
  failedStepStateReducer,
  getExchangeRateStored,
  getIsRouterDelegated,
  getRouterQueueStep,
  getStakingAllowance,
  runRouterQueueStep,
} from '../../shared/routerQueueCore';
import { invalidateRouterQueries } from '../../shared/routerQueueInvalidation';
import {
  ONE_TOKEN,
  RAO_WEI,
  calculateSlippageFloor,
  computeNativeOutFloor,
  computeRouterDeadline,
  computeWrappedOutFloor,
  floorToRao,
} from '../../shared/routerSlippage';

export interface RouterClosePositionQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

export type ClosePositionRepaySource =
  | { kind: 'native-tao' }
  | { kind: 'native-alpha'; source: BittensorNativeSupplySource };

interface ExecuteRouterClosePositionQueueInput {
  repayAmountMantissa: bigint;
  repaySource: ClosePositionRepaySource;
  repayVToken: VToken;
  redeemUnderlyingAmountMantissa: bigint;
  redeemFullSupply: boolean;
  redeemVToken: VToken;
  redeemUnwrapNative: boolean;
  slippagePercentage: number;
  repayFullLoan?: boolean;
}

export const useRouterClosePositionQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterClosePositionQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    repayAmountMantissa,
    repaySource,
    repayVToken,
    redeemUnderlyingAmountMantissa,
    redeemFullSupply,
    redeemVToken,
    redeemUnwrapNative,
    slippagePercentage,
    repayFullLoan = false,
  }: ExecuteRouterClosePositionQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const repaySymbol = repayVToken.underlyingToken.symbol;
      const redeemSymbol = redeemVToken.underlyingToken.symbol;

      // Full-repay buffer covers accrued interest between sign and mine.
      // Router refunds over-payment; safe to overshoot.
      const targetedRepayAmount = repayFullLoan
        ? buffer({ amountMantissa: repayAmountMantissa })
        : repayAmountMantissa;

      const isDelegated = await getIsRouterDelegated({
        accountAddress,
        publicClient,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
      });

      let stakingAllowance = 0n;
      if (repaySource.kind === 'native-alpha') {
        stakingAllowance = await getStakingAllowance({
          accountAddress,
          publicClient,
          routerAddress: routerAddress as Address,
          netuid: repaySource.source.netuid,
        });
      }

      const setupSteps: RouterClosePositionQueuePlanStep[] = [];
      if (repaySource.kind === 'native-alpha') {
        setupSteps.push({
          id: 'approve-native-alpha',
          label: 'Approve native alpha spend',
          shouldSkip: stakingAllowance >= targetedRepayAmount,
          status: 'idle',
        });
      }

      const plan: RouterClosePositionQueuePlanStep[] = [
        {
          id: 'approve-delegate',
          label: 'Approve PositionRouter as delegate',
          shouldSkip: !!isDelegated,
          status: 'idle',
        },
        ...setupSteps,
        {
          id: 'router-close-position',
          label: `Repay ${repaySymbol} + redeem ${redeemSymbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      await runStep({
        ...getRouterQueueStep(plan, 'approve-delegate'),
        action: () =>
          walletClient.writeContract({
            address: poolComptrollerAddress as Address,
            abi: isolatedPoolComptrollerAbi,
            functionName: 'updateDelegate',
            args: [routerAddress as Address, true],
            account: accountAddress,
          }),
      });

      if (repaySource.kind === 'native-alpha') {
        await runStep({
          ...getRouterQueueStep(plan, 'approve-native-alpha'),
          action: () =>
            walletClient.writeContract({
              address: STAKING_V2_ADDRESS as Address,
              abi: stakingV2Abi,
              functionName: 'approve',
              args: [
                routerAddress as Address,
                repaySource.source.netuid,
                BigInt(MAX_UINT256.toFixed()),
              ],
              account: accountAddress,
            }),
        });
      }

      let repayMinSharesOut: bigint;
      let effectiveRepayAmount = targetedRepayAmount;
      if (repaySource.kind === 'native-tao') {
        effectiveRepayAmount = floorToRao(targetedRepayAmount);
        // Compare against floored PRE-buffer so a sub-RAO buffer that floors
        // to zero is detected and padded to +1 RAO. See useRouterRepayQueue.
        if (repayFullLoan && effectiveRepayAmount <= floorToRao(repayAmountMantissa)) {
          effectiveRepayAmount = floorToRao(repayAmountMantissa) + RAO_WEI;
        }
        if (effectiveRepayAmount === 0n) {
          throw new Error('TAO repay amount must be at least 1 RAO (1e-9 TAO)');
        }
        const wsTaoRate = await publicClient.readContract({
          address: repayVToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        repayMinSharesOut = computeWrappedOutFloor({
          nativeAmount: effectiveRepayAmount,
          exchangeRate: wsTaoRate,
          slippagePercentage,
        });
      } else {
        // Live wAlpha rate read (block-by-block accrual on chain 945).
        const wAlphaRate = await publicClient.readContract({
          address: repayVToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
        repayMinSharesOut = computeWrappedOutFloor({
          nativeAmount: effectiveRepayAmount,
          exchangeRate: wAlphaRate,
          slippagePercentage,
        });
      }

      let redeemShares: bigint;
      let expectedWrapperOut: bigint;
      const vTokenRate = await getExchangeRateStored({
        publicClient,
        vTokenAddress: redeemVToken.address,
      });
      if (vTokenRate <= 0n) {
        throw new Error('Invalid vToken exchange rate');
      }
      if (redeemFullSupply) {
        redeemShares = BigInt(MAX_UINT256.toFixed());
        const onChainVTokenBalance = await publicClient.readContract({
          address: redeemVToken.address,
          abi: vBep20Abi,
          functionName: 'balanceOf',
          args: [accountAddress],
        });
        expectedWrapperOut = (onChainVTokenBalance * vTokenRate) / ONE_TOKEN;
      } else {
        redeemShares =
          (redeemUnderlyingAmountMantissa * ONE_TOKEN + (vTokenRate - 1n)) / vTokenRate;
        expectedWrapperOut = redeemUnderlyingAmountMantissa;
      }

      const redeemWrapperKind = await getVTokenWrapperKind({
        publicClient,
        underlyingAddress: redeemVToken.underlyingToken.address,
      });
      if (redeemUnwrapNative && redeemWrapperKind === 'unsupported') {
        throw new Error(
          `Close-position redeem leg is not supported for ${redeemSymbol}: underlying is neither a wsTAO nor a wAlpha wrapper.`,
        );
      }
      const isWsTAORedeem = redeemWrapperKind === 'wsTAO';
      let redeemMinOut: bigint;
      if (redeemUnwrapNative && !isWsTAORedeem) {
        const wAlphaRate = await publicClient.readContract({
          address: redeemVToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
        redeemMinOut = computeNativeOutFloor({
          wrapperAmount: expectedWrapperOut,
          exchangeRate: wAlphaRate,
          slippagePercentage,
        });
      } else if (redeemUnwrapNative && isWsTAORedeem) {
        const wsTaoRate = await publicClient.readContract({
          address: redeemVToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        redeemMinOut = computeNativeOutFloor({
          wrapperAmount: expectedWrapperOut,
          exchangeRate: wsTaoRate,
          slippagePercentage,
        });
      } else {
        redeemMinOut = calculateSlippageFloor({
          amount: expectedWrapperOut,
          slippagePercentage,
        });
      }

      const deadline = computeRouterDeadline();
      const closePositionStep = getRouterQueueStep(plan, 'router-close-position');

      await runStep({
        ...closePositionStep,
        action: () => {
          const repayLeg = {
            vToken: repayVToken.address,
            amount: repaySource.kind === 'native-tao' ? 0n : effectiveRepayAmount,
            isNative: repaySource.kind === 'native-tao',
            minSharesOut: repayMinSharesOut,
          } as const;
          const redeemLeg = {
            vToken: redeemVToken.address,
            shares: redeemShares,
            unwrapNative: redeemUnwrapNative,
            minOut: redeemMinOut,
          } as const;

          if (repaySource.kind === 'native-tao') {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'closePosition',
              args: [repayLeg, redeemLeg, deadline],
              value: effectiveRepayAmount,
              account: accountAddress,
            });
          }
          return walletClient.writeContract({
            address: routerAddress as Address,
            abi: positionRouterAbi,
            functionName: 'closePosition',
            args: [repayLeg, redeemLeg, deadline],
            account: accountAddress,
          });
        },
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [repayVToken.address, redeemVToken.address],
        netuid: repaySource.kind === 'native-alpha' ? repaySource.source.netuid : undefined,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
